import React from 'react'
import NextLink from 'next/link'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import CircularProgressV2 from 'app/KinerjaPSPage/components/CircularProgressV2'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import { useSummary } from 'app/KinerjaTimPenilaiPSPage/hooks/useSummary'
import useBasePath from 'app/KinerjaTimPenilaiPSPage/hooks/useBasePath'
import useToggleSdkContext from 'utils/useToogleSdk'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'
import { useAPIGetPenilaianPeriodik } from 'api/kinerja/tim-penilai-ps/penilaian-periodik'
import { capitalizeFirstLetter } from 'utils/text'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { useAPIGetPenetapanPegawai } from 'api/kinerja/tim-penilai-ps/penetapan'

type TMenuCardProps = {
  isLocked?: boolean
  title: string
  subtitle?: string
  href?: string
  progress?: {
    value: number
    maxValue: number
  }
}
const MenuCard: React.FC<TMenuCardProps> = ({
  isLocked = false,
  title,
  subtitle,
  href,
  progress,
}) => {
  return (
    <div className="flex items-center justify-between gap-6 p-6 bg-white rounded-md shadow-md">
      <div className="w-[84px] h-[84px] flex-none">
        <CircularProgressV2
          value={progress?.value ?? 0}
          maxValue={progress?.maxValue ?? 0}
          isLocked={isLocked}
          textVariant="heading-sm"
        />
      </div>
      <div className="col-span-10 space-y-1 grow">
        <div>
          <Text variant="display-sm">{title}</Text>
        </div>
        {Boolean(subtitle) && (
          <Badge>
            <Text variant="body-sm-bold">{subtitle}</Text>
          </Badge>
        )}
      </div>
      {!isLocked && Boolean(href) && (
        <div className="flex-none">
          <NextLink href={href}>
            <Button as="a" size="lg" className="w-32" color="white">
              Cek
            </Button>
          </NextLink>
        </div>
      )}
    </div>
  )
}

function TimPenilaiPSIndexPage() {
  const toggle = useToggleSdkContext()
  const penilaianEnabled =
    toggle?.['kinerja-ps-tim-penilai-ps-penilaian-enabled'] || false
  const penetapanEnabled =
    toggle?.['kinerja-ps-tim-penilai-ps-penetapan-enabled'] || false
  const penilaianPeriodikEnabled =
    toggle?.['kinerja-tim-penilai-ps-penilaian-periodik-enabled'] || false
  const { trackEventBase } = usePPSTrackEvent()

  const {
    basePath,
    isPPKPengawasSekolahUrl: isPPKPengawasSekolah,
    isTimKinerjaPengawasSekolahUrl: isTimKinerjaPengawasSekolah,
  } = useBasePath()

  // Summary API
  const { apiStatus, summary } = useSummary()

  // Penilaian Periodik API
  const { data: penilaianPeriodikData, isLoading: isPenilaianPeriodikLoading } =
    useAPIGetPenilaianPeriodik({
      enabled: penilaianPeriodikEnabled,
    })

  // Get Penetapan Pegawai List
  const { data: penetapanRatedByPPK } = useAPIGetPenetapanPegawai({
    params: {
      status: 'PPK_SENT',
    },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const showPerencanaanSubtitle =
    Boolean(summary?.perencanaan?.done && summary?.perencanaan?.total) &&
    summary?.perencanaan?.done < summary?.perencanaan?.total
  const showPenetapanRatedByPPKEntryPoint = React.useMemo(() => {
    return isTimKinerjaPengawasSekolah && penetapanRatedByPPK?.meta?.total > 0
  }, [isTimKinerjaPengawasSekolah, penetapanRatedByPPK?.meta?.total])

  const showPenilaianPeriodikEntryPoint = React.useMemo(() => {
    if (isPenilaianPeriodikLoading) {
      return false
    }

    return (
      isPPKPengawasSekolah &&
      penilaianPeriodikEnabled &&
      ['BULANAN', 'TRIWULAN'].includes(penilaianPeriodikData?.tipePeriodik)
    )
  }, [
    isPPKPengawasSekolah,
    isPenilaianPeriodikLoading,
    penilaianPeriodikData?.tipePeriodik,
    penilaianPeriodikEnabled,
  ])

  React.useEffect(() => {
    trackEventBase('beranda', 'view')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (apiStatus === 'loading') {
    return null
  }

  return (
    <div className="pl-64">
      <KinerjaSidebar
        sticky
        activeMenu={
          isPPKPengawasSekolah ? 'pejabat-penilai-kinerja-ps' : 'tim-kinerja-ps'
        }
      />
      <div className="w-full gap-4 p-6 space-y-4 xl:space-y-0 xl:flex">
        <div className="w-9/12 space-y-4">
          <MenuCard
            title="Perencanaan Kinerja"
            subtitle={
              showPerencanaanSubtitle
                ? `${summary?.perencanaan?.done ?? 0}/${
                    summary?.perencanaan?.total ?? 0
                  } Perencanaan sudah disetujui`
                : null
            }
            href={`${basePath}/penilai/perencanaan`}
            progress={{
              value: summary?.perencanaan?.done,
              maxValue: summary?.perencanaan?.total,
            }}
          />
          <MenuCard
            title={
              isPPKPengawasSekolah ? 'Penilaian Kinerja' : 'Pemantauan Kinerja'
            }
            isLocked={!penilaianEnabled || !summary?.penilaian?.total}
            href={`${basePath}/penilai/pelaksanaan/beranda`}
            progress={{
              value: summary?.penilaian?.done,
              maxValue: summary?.penilaian?.total,
            }}
          />
          <MenuCard
            title={
              isPPKPengawasSekolah
                ? 'Penetapan Predikat Kinerja'
                : 'Rekomendasi Predikat Kinerja'
            }
            isLocked={!penetapanEnabled || !summary?.penetapan?.total}
            href={`${basePath}/penilai/penetapan`}
            progress={{
              value: summary?.penetapan?.done,
              maxValue: summary?.penetapan?.total,
            }}
          />

          {showPenetapanRatedByPPKEntryPoint && (
            <SimpleCard className="p-6 space-y-6 bg-white !border-none rounded-md shadow-md">
              <div className="flex items-center justify-between space-x-6">
                <Text variant="display-sm" className="max-w-lg">
                  Penetapan Predikat Kinerja dari Pejabat Penilai Kinerja
                </Text>
                <div className="flex-none">
                  <NextLink
                    href={`${basePath}/penilai/penetapan/ditetapkan-ppk`}
                  >
                    <Button as="a" size="lg" className="w-32" color="black">
                      Cek
                    </Button>
                  </NextLink>
                </div>
              </div>

              <div className="max-w-2xl">
                <Text color="subdued">
                  Hasil Penilaian Kinerja sudah dikirim ke Kepala Sekolah dan
                  tidak dapat diubah lagi. Data mungkin muncul secara berkala.
                  <br />
                  <br />
                  Dengan ini, seluruh proses Pengelolaan Kinerja periode ini
                  dinyatakan <strong>selesai</strong>.
                </Text>
              </div>
            </SimpleCard>
          )}
        </div>

        {showPenilaianPeriodikEntryPoint && (
          <div className="w-3/12">
            <div className="p-6 bg-white rounded-md shadow-md">
              <div className="flex mb-2 gap-x-2">
                <Text variant="action">Penilaian Periodik</Text>
                <Badge className="!text-inverse !bg-[#C68B24] !border-none">
                  {capitalizeFirstLetter(
                    (penilaianPeriodikData?.tipePeriodik ?? '').toLowerCase()
                  )}
                </Badge>
              </div>

              <div className="mb-5">
                <Text variant="body-sm" color="subdued">
                  Dilakukan sebagai alat bantu (tidak wajib) untuk memenuhi{' '}
                  <strong>
                    kebutuhan administrasi kepegawaian sesuai kebijakan
                    KemenPANRB, BKN, dan kebijakan Pemerintah Daerah.
                  </strong>
                </Text>
              </div>

              <div>
                <NextLink href={`${basePath}/penilai/penilaian/periodik`}>
                  <Button color="white" size="sm">
                    Lakukan Penilaian
                  </Button>
                </NextLink>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TimPenilaiPSIndexPage
